import CompliancePage, {
  CompliancePageContent,
} from '@comp/compliance/compliance-template'

const en = (text: string) => ({ en: text, tr: '' })

const content: CompliancePageContent = {
  path: '/compliance/iec-62443',
  bilingual: false,
  seoTitle: en('IEC 62443 Compliance: Identity & Access Controls for OT | Monofor'),
  seoDescription: en(
    'Map IEC 62443-3-3 identification, use-control, and audit requirements to enforced controls: individual identities, vendor access with asset-owner approval, session recording, and air-gapped deployment.'
  ),
  heroEyebrow: en('IEC 62443 compliance'),
  heroTitle: en('The standard says the asset owner controls access. Make it true.'),
  heroBody: en(
    'IEC 62443 puts identification, authentication, use control, and auditable events at the core of industrial cybersecurity, and expects service-provider access to happen on the asset owner’s terms. Monofor enforces those controls on the access path itself: individual identities, approval-gated vendor sessions, full recording, all deployable air-gapped next to the plant.'
  ),
  contextHeading: en('Where IEC 62443 meets identity'),
  contextBody: en(
    'IEC 62443 is the international standard family for securing industrial automation and control systems (IACS). Part 3-3 defines system security requirements (SRs) organized under foundational requirements such as identification and authentication control (FR1) and use control (FR2), graded by security levels; part 2-4 defines what asset owners should expect from the security programs of their service providers, including how remote access is consented to and controlled. Regulators lean on it too: for operators in NIS2 scope with OT estates, 62443 is the de facto answer to "how" for the directive’s technical measures.\n\nA large share of the SRs auditors actually test are identity questions: are people individually identified and authenticated, are shared accounts gone, is remote access from untrusted networks restricted and terminable, is every privileged action attributable in an audit trail? Those are enforced by the access platform, not written in a policy binder. And because control networks are segmented or fully air-gapped by design, the platform enforcing them has to run inside the plant’s own zones, without a cloud dependency.'
  ),
  requirementsHeading: en('SRs to controls, mapped.'),
  requirementsBody: en(
    'The IEC 62443-3-3 system requirements and 62443-2-4 program expectations with an identity dimension, and the Monofor capability that enforces each one.'
  ),
  requirements: [
    {
      requirement: en('SR 1.1 / 1.2: Human and process identification & authentication'),
      detail: en(
        'Every human user and software process is uniquely identified and authenticated.'
      ),
      capability: en(
        'Every operator, engineer, and vendor connects with an individual, verified identity; shared "maintenance" accounts disappear because target credentials stay in the vault and are injected by the platform. External users enroll passwordless via magic link.'
      ),
      productHref: '/pam/vendor-access',
      productName: 'Vendor Privileged Access',
    },
    {
      requirement: en('SR 1.5 / 1.7: Authenticator management and strength'),
      detail: en(
        'Authenticators are protected, managed, and strong enough for the security level.'
      ),
      capability: en(
        'Target-system credentials live in an encrypted vault with rotation, and are never disclosed to the user; human authentication happens at the identity layer with MFA including phishing-resistant FIDO2 passkeys, independent of what the PLC-era target supports.'
      ),
      productHref: '/pam/vault',
      productName: 'Monopam Vault',
    },
    {
      requirement: en('SR 1.13: Access via untrusted networks'),
      detail: en(
        'Access from outside the control zone is explicitly restricted, monitored, and controlled.'
      ),
      capability: en(
        'Remote sessions terminate at the Monopam gateway in the industrial DMZ; nothing tunnels into the control zone. Per-vendor network allow-lists (IP/CIDR) gate both sign-in and connection start, and unreadable client addresses fail closed.'
      ),
      productHref: '/pam/gateway',
      productName: 'Monopam Gateway',
    },
    {
      requirement: en('SR 2.1: Authorization enforcement (least privilege)'),
      detail: en(
        'Users get the permissions their role needs on the assets in scope, nothing more.'
      ),
      capability: en(
        'Access is granted per resource, not per network; vendor organizations scope who may reach what, and just-in-time approval puts a human decision in front of a connection when you require it.'
      ),
      productHref: '/pam/jit-access',
      productName: 'Just-in-time access',
    },
    {
      requirement: en('SR 2.6: Remote session termination'),
      detail: en(
        'Remote sessions can be terminated on demand and end automatically with the authorization.'
      ),
      capability: en(
        'Live sessions are visible and terminable with one click; expiry, inactivity rules, deactivation, and access-review rejections cut running sessions automatically the moment the authorization ends.'
      ),
      productHref: '/pam/vendor-access',
      productName: 'Vendor Privileged Access',
    },
    {
      requirement: en('SR 2.8-2.12, SR 6.1: Auditable events and audit accessibility'),
      detail: en(
        'Security-relevant events are recorded, protected, and accessible for analysis.'
      ),
      capability: en(
        'Every privileged session is recorded (video plus keystrokes for interactive types, statement-level audit for database sessions), tied to an individual identity, and the full lifecycle trail (invite, approval, connection, expiry, review) exports to your SIEM.'
      ),
      productHref: '/pam/session-recording',
      productName: 'Session recording',
    },
    {
      requirement: en('62443-2-4 SP.05 / SP.07: Remote access on the asset owner’s terms'),
      detail: en(
        'Service-provider access requires asset-owner consent and remains under asset-owner control.'
      ),
      capability: en(
        'Vendor invitations pass your approval flow before an account exists, every connection can require a per-session approval, the sponsor is notified on connect, and the approval trail names who consented to what, when.'
      ),
      productHref: '/pam/vendor-access',
      productName: 'Vendor Privileged Access',
    },
  ],
  faqs: [
    {
      q: en('Does deploying Monofor make us IEC 62443 compliant?'),
      a: en(
        'No product does. 62443 also covers network zoning (zones and conduits), system hardening, patch management, and organizational processes. Monofor enforces the identification, use-control, and audit requirements on the access path, which is a large share of what assessments test, and produces the per-session evidence assessors ask for.'
      ),
    },
    {
      q: en('Does this work in an air-gapped or fully segmented plant?'),
      a: en(
        'Yes. Monofor deploys self-hosted inside your own zones, typically with the gateway in the industrial DMZ, with no SaaS or outbound cloud dependency on the access path. That is a deliberate design target: OT-focused remote-access tools that relay sessions through a vendor cloud cannot serve an air-gapped site.'
      ),
    },
    {
      q: en('How does IEC 62443 relate to NIS2?'),
      a: en(
        'NIS2 tells essential and important entities what outcomes they must achieve; for operators with OT estates, IEC 62443 is the widely accepted answer to how, and national authorities reference it in guidance. The same Monofor controls serve both: MFA, supplier access management, and auditable evidence map to NIS2 Article 21 as well; see our NIS2 page for that mapping.'
      ),
    },
    {
      q: en('We only need vendor remote access, not a full PAM program. Where do we start?'),
      a: en(
        'Start exactly there: model each supplier as a vendor organization, route their sessions through the gateway, and let expiry and access reviews govern the lifecycle. The 62443-relevant evidence (individual identities, asset-owner approvals, recordings) is produced from day one, and the same platform extends to internal privileged access when you are ready.'
      ),
    },
  ],
}

type Props = { locale: string }

export default function Iec62443CompliancePage({ locale }: Props) {
  return <CompliancePage content={content} locale={locale} />
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
